import React, { useEffect, useRef, useState } from "react";
import { useStores } from "../../../use-stores";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Collapse,
  Container,
  Grid2,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  useTheme,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { observer } from "mobx-react";
import { FilterValue } from "../../../models/Filters/FilterValue";
import AppDropDown from "../../components/common/filter/AppDropDown";
import { CouponFilters } from "../../../models/inventory/CouponsFilters";
import AppSearchDropDown from "../../components/common/filter/AppSearchDropDown";
import { SortType } from "../../../models/SortType";
import CouponsTable from "../../components/Inventory/CouponsTable";
import { useNotification } from "../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import { Coupon } from "../../../models/inventory/Coupon";
import FormTextField from "../../components/common/form/FormTextField";
import AppButton from "../../components/common/buttons/AppButton";
import PermissionBlockLabel from "../../components/common/pop-up/PermissionBlockLabel ";
import CustomTypography from "../../components/common/form/CustomTypography";
import CouponDetailsPopup from "../../components/Inventory/CouponDetailsPopup";

interface CouponsSelectedFilters {
  order_id: FilterValue[];
  customer_id: FilterValue[];
  inventory_id: FilterValue[];
  status: string;
  active: boolean;
  is_onelink: string | null;
}

const ACTION_OPTIONS: FilterValue[] = [
  { id: "send_reminders", value: "Send Reminders" },
  { id: "download_shipments", value: "Download Shipments" },
  { id: "download_report", value: "Download Report" },
  { id: "expire", value: "Expire" },
  { id: "reactivate", value: "Reactivate" },
];

const CouponsListing: React.FC = () => {
  const theme = useTheme();
  const { couponStore, customerStore, inventoryStore, orderStore, userStore } =
    useStores();

  const { showError, showWarning, showSuccess } = useNotification();

  const [selectedShipmentIdsMap, setSelectedShipmentIdsMap] = useState<
    Map<number, number>
  >(new Map<number, number>());
  const actionsAnchorRef = useRef<HTMLDivElement | null>(null);
  const [actionsMenuAnchor, setActionsMenuAnchor] =
    useState<HTMLElement | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const initialFilters: CouponsSelectedFilters = {
    order_id: searchParams.get("order_id")
      ? searchParams
          .get("order_id")!
          .split(",")
          .map((id) => ({ id: id, value: id }))
      : [],
    customer_id: searchParams.get("customer_id")
      ? searchParams
          .get("customer_id")!
          .split(",")
          .map((item) => {
            const list = item.split(":")!;
            return { id: list[0], value: list[1] };
          })
      : [],
    inventory_id: searchParams.get("inventory_id")
      ? searchParams
          .get("inventory_id")!
          .split(",")
          .map((id) => ({ id: id, value: id }))
      : [],
    status: searchParams.get("status") ? searchParams.get("status")! : "0",
    active: searchParams.get("active")
      ? searchParams.get("active") === "false"
        ? false
        : true
      : true,
    is_onelink: searchParams.get("is_onelink") || null,
  };

  const initialParams: CouponFilters = {
    page_no: searchParams.get("page")
      ? parseInt(searchParams.get("page") as string, 10)
      : 1,
    search_text: searchParams.get("search_text") || undefined,
    order_id: initialFilters.order_id.map((item) => Number(item.id)),
    customer_id: initialFilters.customer_id.map((item) => Number(item.id)),
    inventory_id: initialFilters.inventory_id.map((item) => Number(item.id)),
    status: searchParams.get("status") ? Number(searchParams.get("status")) : 0,
    active: searchParams.get("active")
      ? searchParams.get("active") === "false"
        ? false
        : true
      : true,
    is_onelink:
      initialFilters.is_onelink === "1"
        ? true
        : initialFilters.is_onelink === "0"
          ? false
          : undefined,
    limit: searchParams.get("limit")
      ? parseInt(searchParams.get("limit") as string, 10)
      : 30,
    sort_by: searchParams.get("sort_by") || "created_date",
    sort_type:
      searchParams.get("sort_type") === "ASC" ? SortType.ASC : SortType.DESC,
  };

  const [filters, setFilters] =
    useState<CouponsSelectedFilters>(initialFilters);
  const [params, setParams] = useState(initialParams);

  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search_text") || "",
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(true);
  const [selectedCouponDetails, setSelectedCouponDetails] =
    useState<Coupon | null>(null);
  const [isDetailsPopupOpen, setIsDetailsPopupOpen] = useState(false);

  const handleActionsMenuOpen = () => {
    if (!actionsAnchorRef.current) return;
    setActionsMenuAnchor(actionsAnchorRef.current);
  };

  const handleActionsMenuClose = () => {
    setActionsMenuAnchor(null);
  };

  const handleExpireSelectedCoupons = () => {
    if (!params.order_id)
      return showWarning(
        "Please make sure order filter is applied. Only one order should selected.",
      );

    if (Array.isArray(params.order_id) && params.order_id.length > 1)
      return showWarning(
        "To expire coupons, please make sure only one order is selected in the filters.",
      );

    if (selectedShipmentIdsMap.size === 0)
      return showWarning("Please select coupons to expire.");

    couponStore
      .expireCoupons(params, Array.from(selectedShipmentIdsMap.keys()))
      .then((response) => {
        showSuccess(response.message);
        handleApply();
      })
      .catch((err) => {
        showError(
          "Error",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        );
      });
  };

  const handleReactivateSelectedCoupons = () => {
    if (!params.order_id)
      return showWarning(
        "Please make sure order filter is applied. Only one order should selected.",
      );

    if (Array.isArray(params.order_id) && params.order_id.length > 1)
      return showWarning(
        "To reactivate coupons, please make sure only one order is selected in the filters.",
      );

    if (selectedShipmentIdsMap.size === 0)
      return showWarning("Please select coupons to reactivate.");

    couponStore
      .reactivateCoupons(params, Array.from(selectedShipmentIdsMap.keys()))
      .then((response) => {
        showSuccess(response.message);
        handleApply();
      })
      .catch((err) => {
        showError(
          "Error",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        );
      });
  };

  const handleActionSelect = (actionId: string) => {
    if (actionId === "send_reminders") {
      handleSendReminders();
    } else if (actionId === "download_shipments") {
      downloadShipmentsReport();
    } else if (actionId === "download_report") {
      downloadReport();
    } else if (actionId === "expire") {
      handleExpireSelectedCoupons();
    } else if (actionId === "reactivate") {
      handleReactivateSelectedCoupons();
    }
    handleActionsMenuClose();
  };

  const handleViewDetails = (coupon: Coupon) => {
    setSelectedCouponDetails(coupon);
    setIsDetailsPopupOpen(true);
  };

  const handleCloseDetails = () => {
    setIsDetailsPopupOpen(false);
    setSelectedCouponDetails(null);
  };

  let apiTimer: any;

  useEffect(() => {
    if (userStore.hasPermission(EmployeeRoles.VIEW_COUPONS)) {
      setIsLoading(true);
      couponStore
        .getCoupons(params)
        .catch((err) =>
          showError(
            "Error",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later.",
          ),
        )
        .finally(() => {
          setIsLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    const updateQueryParams = () => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (
          value !== undefined &&
          (!Array.isArray(value) || value.length > 0)
        ) {
          if (key === "customer_id") {
            const paramValue = filters.customer_id
              .map((item) => item.id + ":" + item.value)
              .join(",");
            searchParams.set(key, paramValue);
          } else if (key === "is_onelink") {
            const onelinkValue = filters.is_onelink;
            if (onelinkValue !== null) {
              searchParams.set("is_onelink", onelinkValue);
            }
          } else {
            const paramValue = Array.isArray(value)
              ? value.join(",")
              : value.toString();
            searchParams.set(key, paramValue);
          }
        }
      });

      const queryString = searchParams.toString();
      if (queryString) {
        navigate({ search: queryString }, { replace: true });
      }
    };

    updateQueryParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, navigate]);

  const handleSearchBar = async (searchText: string | undefined) => {
    if (searchText && searchText.length !== 0) {
      setSearchTerm(searchText);
    } else {
      setSearchTerm("");
      setParams((prev) => ({
        ...prev,
        search_text: "",
      }));
    }
  };

  const handleSearchSubmit = () => {
    setParams((prev) => ({
      ...prev,
      search_text: searchTerm,
      page_no: 1,
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      if (searchTerm.trim().length > 0) {
        setParams((prev) => ({
          ...prev,
          search_text: searchTerm,
          page_no: 1,
        }));
      } else {
        setSearchTerm("");
        setParams((prev) => ({
          ...prev,
          search_text: "",
          page_no: 1,
        }));
      }
    }
  };

  const handleApply = () => {
    setSelectedShipmentIdsMap(new Map());
    setParams((prev) => ({
      ...prev,
      order_id: filters.order_id.map((item) => Number(item.id)),
      inventory_id: filters.inventory_id.map((item) => Number(item.id)),
      customer_id: filters.customer_id.map((item) => Number(item.id)),
      status: Number(filters.status),
      active: filters.active,
      is_onelink:
        filters.is_onelink === "1"
          ? true
          : filters.is_onelink === "0"
            ? false
            : undefined,
    }));
  };

  const resetFilters = () => {
    setSelectedShipmentIdsMap(new Map());
    setSearchTerm("");
    setFilters({
      order_id: [],
      customer_id: [],
      inventory_id: [],
      status: "0",
      active: true,
      is_onelink: null,
    });
    setParams({
      page_no: 1,
      limit: 30,
      sort_by: "created_date",
      sort_type: SortType.DESC,
      order_id: [],
      inventory_id: [],
      customer_id: [],
      status: 0,
      active: true,
      is_onelink: undefined,
    });
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setParams((prev) => ({
      ...prev,
      page_no: newPage,
    }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setParams((prev) => ({
      ...prev,
      limit: newLimit,
      page_no: 1,
    }));
  };

  const handleCustomerSearch = (searchText?: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 2)
      apiTimer = setTimeout(() => {
        customerStore
          .searchCustomers({ page_no: 1, search_text: searchText })
          .catch((err) => {});
      }, 1500);
  };

  const handleOrderIdSearch = (searchText?: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 0)
      apiTimer = setTimeout(() => {
        orderStore.searchOrderIds(searchText).catch((err) => {});
      }, 1500);
  };

  const handleInventoryIdsSearch = (searchText?: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 0)
      apiTimer = setTimeout(() => {
        inventoryStore.searchIds(searchText).catch((err) => {});
      }, 1500);
  };

  const handleSendReminders = () => {
    let allValidIds = true;
    for (let i in couponStore.coupons) {
      const item = couponStore.coupons[i];
      if (selectedShipmentIdsMap.get(item.id)) {
        if (item.redeemed || !item.active) {
          allValidIds = false;
          break;
        }
      }
    }

    if (!allValidIds)
      showWarning("Make sure all selected coupons are valid and not redeemed.");
    else {
      const seletectedCouponIds = Array.from(selectedShipmentIdsMap.keys());
      if (seletectedCouponIds.length === 0) {
        return showWarning("Please select redemptions to send reminders.");
      }

      couponStore
        .sendReminders(seletectedCouponIds)
        .then((response) => {
          const ids = response.result as number[];
          if (ids && ids.length !== 0) {
            const message = `${response.message} Rejected ids are ${ids}`;
            showWarning(message);
          } else {
            showSuccess("Reminders sent.");
            setSelectedShipmentIdsMap(new Map());
          }
        })
        .catch((err) => {
          showError(
            "Error",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later.",
          );
        });
    }
  };

  const downloadShipmentsReport = () => {
    if (!params.customer_id)
      return showWarning(
        "Please make sure customer filter is applied. Only one customer should selected.",
      );

    if (Array.isArray(params.customer_id) && params.customer_id.length > 1)
      return showWarning(
        "Please make sure only one customer is selected in the filters.",
      );

    couponStore
      .downloadShipmentsReport(params)
      .then((response) => {
        if (response.data) {
          const downloadUrl = window.URL.createObjectURL(response.data);
          const a = document.createElement("a");
          a.href = downloadUrl;
          a.download = response.filename
            ? response.filename
            : `RedemptionShipmentsReport.csv`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(downloadUrl);
        } else {
          return showWarning(
            "Failed to download the Coupons shipments report csv file.",
          );
        }
      })
      .catch((err) => {
        showError(
          "Error",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        );
      });
  };

  const downloadReport = () => {
    if (!params.customer_id)
      return showWarning(
        "Please make sure customer filter is applied. Only one customer should selected.",
      );

    if (Array.isArray(params.customer_id) && params.customer_id.length > 1)
      return showWarning(
        "Please make sure only one customer is selected in the filters.",
      );

    couponStore
      .downloadReport(params)
      .then((response) => {
        if (response.data) {
          const downloadUrl = window.URL.createObjectURL(response.data);
          const a = document.createElement("a");
          a.href = downloadUrl;
          a.download = response.filename
            ? response.filename
            : `RedemptionReport.csv`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(downloadUrl);
        } else {
          return showWarning("Failed to download the Coupons report csv file.");
        }
      })
      .catch((err) => {
        showError(
          "Error",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        );
      });
  };

  const renderFilters = () => (
    <Box>
      <Grid2 container spacing={2}>
        <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
          <AppSearchDropDown
            isMultiSelect={true}
            title="Select Inventory Id"
            value={filters.inventory_id}
            fontSize="12px"
            list={inventoryStore.ids.map((item) => {
              return { id: String(item), value: String(item) };
            })}
            local={false}
            handleSearch={handleInventoryIdsSearch}
            handleSelect={(items) => {
              setFilters((prev) => ({
                ...prev,
                inventory_id: items,
              }));
            }}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
          <AppSearchDropDown
            isMultiSelect={true}
            title="Select Order Id"
            value={filters.order_id}
            fontSize="12px"
            list={orderStore.orderIds.map((item) => {
              return { id: String(item), value: String(item) };
            })}
            local={false}
            handleSearch={handleOrderIdSearch}
            handleSelect={(items) => {
              setFilters((prev) => ({
                ...prev,
                order_id: items,
              }));
            }}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
          <AppSearchDropDown
            isMultiSelect={true}
            title="Select Customers"
            value={filters.customer_id}
            fontSize="12px"
            list={customerStore.customers.map((item) => {
              return { id: String(item.id), value: item.name! };
            })}
            local={false}
            handleSearch={handleCustomerSearch}
            handleSelect={(items) => {
              setFilters((prev) => ({
                ...prev,
                customer_id: items,
              }));
            }}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
          <AppDropDown
            title="Status"
            isMultiSelect={false}
            value={filters.status}
            list={[
              { id: "0", value: "Select" },
              { id: "1", value: "Not Sent" },
              { id: "2", value: "Sent" },
              { id: "3", value: "Opened" },
              { id: "4", value: "Pending Redemption" },
              { id: "5", value: "Redeemed" },
              { id: "6", value: "Expired" },
              { id: "7", value: "Reactivated" },
            ]}
            fontSize={"12px"}
            onSingleSelectionChange={(id) => {
              setFilters((prev) => ({
                ...prev,
                status: id,
              }));
            }}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
          <AppDropDown
            title="Active"
            isMultiSelect={false}
            value={filters.active ? "true" : "false"}
            list={[
              { id: "true", value: "Active" },
              { id: "false", value: "InActive" },
            ]}
            fontSize={"12px"}
            onSingleSelectionChange={(id) => {
              setFilters((prev) => ({
                ...prev,
                active: id === "true",
              }));
            }}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
          <AppDropDown
            title="Type"
            isMultiSelect={false}
            value={filters.is_onelink || "-1"}
            list={[
              { id: "-1", value: "Select" },
              { id: "1", value: "OneLink" },
              { id: "0", value: "Redemption" },
            ]}
            fontSize={"12px"}
            onSingleSelectionChange={(id) => {
              setFilters((prev) => ({
                ...prev,
                is_onelink: id ? id : "-1",
              }));
            }}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}></Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            <AppButton
              variant="transparentPrimary"
              label="Clear"
              onClick={resetFilters}
              customStyles={{ minWidth: 120 }}
            />
            <AppButton
              variant="primary"
              label="Apply"
              onClick={handleApply}
              customStyles={{ minWidth: 120 }}
            />
          </Box>
        </Grid2>
      </Grid2>
    </Box>
  );

  const isActionsMenuOpen = Boolean(actionsMenuAnchor);
  const hasAnyActionPermission =
    userStore.hasPermission(EmployeeRoles.SEND_REDEMPTION_REMINDERS) ||
    userStore.hasPermission(EmployeeRoles.DOWNLOAD_REDEMPTION_REPORT);
  const isActionsDisabled = !hasAnyActionPermission;

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }} sx={{ px: 2 }}>
      {!userStore.hasPermission(EmployeeRoles.VIEW_COUPONS) && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Paper
            sx={{
              mt: 3,
              p: { xs: 2, md: 3 },
              borderRadius: 2,
              boxShadow:
                "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 0.4,
              }}
            >
              <CustomTypography variant="heading3">Coupons</CustomTypography>
              <CustomTypography
                variant="body2"
                color={theme.palette.customColors?.greyTextColor}
              >
                Monitor coupon redemptions, reminders, and export detailed
                reports.
              </CustomTypography>
            </Box>
          </Paper>
          <PermissionBlockLabel />
        </Box>
      )}
      {userStore.hasPermission(EmployeeRoles.VIEW_COUPONS) && (
        <Paper
          sx={{
            mt: 3,
            p: { xs: 2, md: 3 },
            borderRadius: 2,
            boxShadow:
              "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 0.5,
              }}
            >
              <CustomTypography variant="heading3">Coupons</CustomTypography>
              <CustomTypography
                variant="body2"
                color={theme.palette.customColors?.greyTextColor}
              >
                Monitor coupon redemptions, reminders, and export detailed
                reports.
              </CustomTypography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: { xs: "flex-start", md: "flex-end" },
                gap: 1,
              }}
            >
              {hasAnyActionPermission && (
                <Box ref={actionsAnchorRef}>
                  <AppButton
                    variant="grayscale"
                    label={`Actions${
                      selectedShipmentIdsMap.size > 0
                        ? ` (${selectedShipmentIdsMap.size})`
                        : ""
                    }`}
                    onClick={handleActionsMenuOpen}
                    disabled={isActionsDisabled}
                    customStyles={{ minWidth: 180 }}
                  />
                </Box>
              )}
            </Box>
            <Menu
              anchorEl={actionsMenuAnchor}
              open={isActionsMenuOpen}
              onClose={handleActionsMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              disableAutoFocusItem
              slotProps={{
                paper: {
                  sx: {
                    mt: 1,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow:
                      "0px 20px 45px rgba(15, 23, 42, 0.15), 0px 6px 18px rgba(15, 23, 42, 0.08)",
                    minWidth: 240,
                    overflow: "hidden",
                  },
                },
              }}
            >
              {ACTION_OPTIONS.filter((option) => {
                if (option.id === "send_reminders") {
                  return userStore.hasPermission(
                    EmployeeRoles.SEND_REDEMPTION_REMINDERS,
                  );
                }
                if (
                  option.id === "download_shipments" ||
                  option.id === "download_report"
                ) {
                  return userStore.hasPermission(
                    EmployeeRoles.DOWNLOAD_REDEMPTION_REPORT,
                  );
                }
                return true; // Expire and Reactivate are always shown
              }).map((option, index) => {
                const requiresSelection =
                  option.id === "send_reminders" ||
                  option.id === "expire" ||
                  option.id === "reactivate";
                const isItemDisabled =
                  requiresSelection && selectedShipmentIdsMap.size === 0;

                return (
                  <MenuItem
                    key={option.id}
                    onClick={() => handleActionSelect(option.id)}
                    disabled={isItemDisabled}
                    sx={{
                      fontSize: "0.9rem",
                      px: 2,
                      py: 1.25,
                      gap: 1,
                      borderTop:
                        index === 0
                          ? "none"
                          : "1px solid " + theme.palette.divider,
                      "&:hover": {
                        backgroundColor:
                          theme.palette.customColors?.lightPurple ||
                          theme.palette.action.hover,
                      },
                      "&.Mui-disabled": {
                        opacity: 0.4,
                      },
                    }}
                  >
                    <CustomTypography variant="body2" fontWeight={600}>
                      {option.value}
                    </CustomTypography>
                  </MenuItem>
                );
              })}
            </Menu>
          </Box>

          <Paper
            variant="outlined"
            sx={{
              mt: 2,
              borderRadius: 2,
              borderColor: "divider",
              overflow: "hidden",
            }}
          >
            <Box
              onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2,
                py: 0.5,
                cursor: "pointer",
                borderBottom: isFiltersExpanded ? "1px solid" : "none",
                borderColor: "divider",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <CustomTypography variant="heading6">
                Search & Filters
              </CustomTypography>
              <IconButton size="small">
                {isFiltersExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            <Collapse in={isFiltersExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 1.5,
                  }}
                >
                  <FormTextField
                    id="coupon-email-search"
                    placeholder="Search by recipient email"
                    type="search"
                    variant="outlined"
                    value={searchTerm}
                    onChange={(_, value) => handleSearchBar(value)}
                    onKeyDown={(e) => handleKeyDown(e)}
                  />
                  <AppButton
                    variant="primary"
                    label="Search"
                    onClick={handleSearchSubmit}
                    customStyles={{ minWidth: 140, height: 40 }}
                  />
                  <AppButton
                    variant="transparentPrimary"
                    label="Reset Search"
                    disabled={!searchTerm || searchTerm === ""}
                    onClick={() => {
                      setSearchTerm("");
                      setParams((prev) => ({
                        ...prev,
                        search_text: "",
                        page_no: 1,
                      }));
                    }}
                    customStyles={{ minWidth: 140, height: 40 }}
                  />
                </Box>
                {renderFilters()}
              </Box>
            </Collapse>
          </Paper>

          {isLoading && (
            <Container
              sx={{
                minHeight: "40vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </Container>
          )}

          {!isLoading && (
            <Box mt={3}>
              {couponStore.coupons.length === 0 && (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="10rem"
                  textAlign="center"
                  border="1px dashed"
                  borderColor="divider"
                  borderRadius={2}
                >
                  <CustomTypography
                    variant="heading6"
                    fontWeight={500}
                    color={theme.palette.customColors?.greyTextColor}
                  >
                    No coupons match the current filters.
                  </CustomTypography>
                </Box>
              )}
              {couponStore.coupons.length > 0 && (
                <CouponsTable
                  maxHeight={window.innerHeight - 350}
                  coupons={couponStore.coupons}
                  allSelected={
                    selectedShipmentIdsMap.size === couponStore.coupons.length
                  }
                  limit={params.limit || couponStore.limit}
                  page_no={params.page_no}
                  total_count={couponStore.total_count}
                  total_pages={couponStore.total_pages}
                  sortBy={params.sort_by}
                  sortType={params.sort_type}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  onSortChange={(column, sortType) => {
                    setParams((prev) => ({
                      ...prev,
                      sort_by: column,
                      sort_type: sortType,
                    }));
                  }}
                  selectedShipmentIdsMap={selectedShipmentIdsMap}
                  selectAllItem={() => {
                    const map = new Map<number, number>();
                    couponStore.coupons.forEach((item) =>
                      map.set(item.id, item.id),
                    );
                    setSelectedShipmentIdsMap(map);
                  }}
                  deSelectAllItem={() => {
                    setSelectedShipmentIdsMap(new Map());
                  }}
                  selectItem={(item) => {
                    const map = new Map(selectedShipmentIdsMap);
                    map.set(item.id, item.id);
                    setSelectedShipmentIdsMap(map);
                  }}
                  deSelectItem={(item) => {
                    const map = new Map(selectedShipmentIdsMap);
                    map.delete(item.id);
                    setSelectedShipmentIdsMap(map);
                  }}
                  onViewDetails={handleViewDetails}
                />
              )}
            </Box>
          )}
        </Paper>
      )}
      <CouponDetailsPopup
        open={isDetailsPopupOpen}
        coupon={selectedCouponDetails}
        onClose={handleCloseDetails}
      />
    </Grid2>
  );
};

export default observer(CouponsListing);
